import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { getEditLockStatusApi, acquireEditLockApi, releaseEditLockApi } from "@/api/encounterNotes";

/**
 * Composable for managing encounter note edit locks.
 * Handles acquiring/releasing locks, visibility changes, and same-user detection.
 *
 * @param {Object} options
 * @param {import('vue-router').RouteLocationNormalizedLoaded} options.route
 * @param {import('vuex').Store} options.store
 * @param {import('vue').Ref<boolean>} options.isInitializing - true while note is loading
 * @param {import('vue').ComputedRef<boolean>} options.isDraft - whether note is unsigned
 * @param {Function} [options.onPageActive] - called when page becomes visible (e.g., to refresh note data)
 */
export function useEditLock({ route, store, isInitializing, isDraft, onPageActive }) {
  const isEditLocked = ref(false);
  const isCheckingLock = ref(false);
  const heldLockNoteId = ref(null);
  const lockedAt = ref(null);
  const userInfo = computed(() => store.state.login?.userInfo);

  // Wait for userInfo to become available in the store
  const waitForUserInfo = () => {
    if (userInfo.value) return Promise.resolve(userInfo.value);
    return new Promise((resolve) => {
      const unwatch = watch(userInfo, (val) => {
        if (val) {
          unwatch();
          resolve(val);
        }
      });
    });
  };

  const checkAndAcquireLock = async (noteId) => {
    if (!noteId || !isDraft.value) return;
    isCheckingLock.value = true;
    isEditLocked.value = true; // Keep locked until acquire succeeds
    try {
      const currentUserInfo = await waitForUserInfo();
      const status = await getEditLockStatusApi(noteId);
      const currentUserId = `${currentUserInfo.customer_id}_${currentUserInfo.clinic_id}`;
      const isLockedByOther = status?.holder?.userId
        && String(status.holder.userId) !== currentUserId;
      if (!isLockedByOther) {
        const lockResponse = await acquireEditLockApi(noteId);
        heldLockNoteId.value = noteId;
        lockedAt.value = lockResponse?.lockedAt || null;
        isEditLocked.value = false;
      } else {
        heldLockNoteId.value = null;
        lockedAt.value = null;
      }
    } catch (error) {
      console.error('Failed to check/acquire edit lock:', error);
      // Acquire failed — stay locked
    } finally {
      isCheckingLock.value = false;
    }
  };

  const releaseLock = async () => {
    if (!heldLockNoteId.value) return;
    const noteId = heldLockNoteId.value;
    heldLockNoteId.value = null;
    try {
      await releaseEditLockApi(noteId);
    } catch (error) {
      console.error('Failed to release edit lock:', error);
    }
  };

  // Acquire lock after note finishes loading
  watch(isInitializing, async (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      await releaseLock();
      await checkAndAcquireLock(route.params.noteId);
    }
  });

  // Handle tab visibility changes
  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
      await onPageActive?.();
      await checkAndAcquireLock(route.params.noteId);
    } else {
      await releaseLock();
    }
  };

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

  onBeforeUnmount(async () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    await releaseLock();
  });

  return {
    isEditLocked,
    isCheckingLock,
    lockedAt,
    releaseLock,
  };
}
